import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Ban, RotateCcw } from 'lucide-react';

export default function AdminProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product,  setProduct]  = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [reason,   setReason]   = useState('');
  const [acting,   setActing]   = useState(false);
  const [activeImg,setActiveImg]= useState(0);

  useEffect(() => {
    Promise.all([
      api.get(`/products/${id}`),
      api.get(`/admin/products/${id}/contacts`),
    ]).then(([pRes, cRes]) => {
      setProduct(pRes.data.product);
      setContacts(cRes.data.contacts);
    }).catch(() => toast.error('Could not load product.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleUnlist = async () => {
    if (!reason.trim()) { toast.error('Please enter a reason.'); return; }
    if (!window.confirm('Unlist this product and notify the seller?')) return;
    setActing(true);
    try {
      await api.put(`/admin/products/${id}/unlist`, { reason });
      setProduct(p => ({ ...p, status: 'removed_by_admin', removalReason: reason }));
      setReason('');
      toast.success('Product unlisted. Seller notified.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed.');
    } finally { setActing(false); }
  };

  const handleRestore = async () => {
    if (!window.confirm('Restore this product to active?')) return;
    setActing(true);
    try {
      await api.put(`/admin/products/${id}/restore`);
      setProduct(p => ({ ...p, status: 'active', removalReason: '' }));
      toast.success('Product restored.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed.');
    } finally { setActing(false); }
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;
  if (!product) return <div className="empty-state"><h4>Product not found</h4></div>;

  return (
    <div className="fade-in">
      <button className="btn btn-ghost btn-icon" style={{ marginBottom: 24 }} onClick={() => navigate(-1)}>
        <ArrowLeft size={20} />
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 }}>

        {/* Images */}
        <div>
          <div style={{ aspectRatio: '4/3', borderRadius: 'var(--radius-lg)', overflow: 'hidden', background: 'var(--bg-2)', marginBottom: 12, border: '1px solid var(--border)' }}>
            <img src={product.images[activeImg]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          {product.images.length > 1 && (
            <div style={{ display: 'flex', gap: 8 }}>
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)}
                  style={{ width: 56, height: 56, borderRadius: 8, overflow: 'hidden', border: `2px solid ${activeImg === i ? 'var(--accent)' : 'var(--border)'}`, padding: 0, background: 'none', cursor: 'pointer' }}>
                  <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span className={`badge badge-${product.category}`}>{product.category}</span>
            <span className={`badge ${product.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
              {product.status}
            </span>
          </div>

          <h2 style={{ fontFamily: 'var(--font-display)' }}>{product.title}</h2>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', color: 'var(--accent)' }}>
            ₹{product.price.toLocaleString()}
          </div>
          <p style={{ fontSize: '0.875rem', lineHeight: 1.7, color: 'var(--text-muted)' }}>{product.description}</p>

          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            <p>Seller: <strong>{product.seller?.name}</strong> · {product.seller?.rollNumber}</p>
            <p>Interests: <strong>{product.contactCount}</strong></p>
            <p>Qty: <strong>{product.quantity}</strong></p>
            {product.removalReason && (
              <p style={{ color: 'var(--danger)', marginTop: 8 }}>Removal reason: {product.removalReason}</p>
            )}
          </div>

          {/* Admin actions */}
          {product.status === 'active' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
              <label className="label">Reason for removal (required)</label>
              <textarea className="textarea"
                placeholder="e.g. Inappropriate content, prohibited item..."
                value={reason}
                onChange={e => setReason(e.target.value)}
                rows={3}
              />
              <button className="btn btn-danger" onClick={handleUnlist} disabled={acting}>
                {acting ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Unlisting...</> : <><Ban size={15} /> Unlist & Notify Seller</>}
              </button>
            </div>
          ) : product.status === 'removed_by_admin' ? (
            <button className="btn btn-success" onClick={handleRestore} disabled={acting}>
              {acting ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Restoring...</> : <><RotateCcw size={15} /> Restore Product</>}
            </button>
          ) : null}
        </div>
      </div>

      {/* Contacts table */}
      <h3 style={{ marginBottom: 16 }}>Buyers Who Contacted for This Product ({contacts.length})</h3>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>Buyer Name</th><th>Roll No.</th><th>WhatsApp</th><th>Call</th><th>Date</th></tr>
          </thead>
          <tbody>
            {contacts.length === 0
              ? <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>No contacts yet</td></tr>
              : contacts.map(c => (
                <tr key={c._id}>
                  <td style={{ fontWeight: 600 }}>{c.buyerSnapshot?.name}</td>
                  <td>{c.buyerSnapshot?.rollNumber}</td>
                  <td>{c.buyerSnapshot?.whatsappNumber}</td>
                  <td>{c.buyerSnapshot?.callNumber}</td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                    {new Date(c.createdAt).toLocaleDateString('en-IN')}
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
    </div>
  );
}